import React, { useState, useEffect } from "react";
import { FileText, Calendar, User, Eye, X, Package, Edit, AlertCircle, FolderOpen, Folder } from "lucide-react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";
// import { ModalDialog } from './NotificationSystem';

const Arkiv: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  setCustomers,
  openCaseForEditing,
  archivedCases = [], // LAGT TIL: prop for arkiverte cases
  setArchivedCases,
  selectedCaseId,  
onCaseViewed,   
}) => {
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isMoving, setIsMoving] = useState<number | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [activeFolder, setActiveFolder] = useState<"alle" | "reklamasjon">("alle");
  const [showLockedDialog, setShowLockedDialog] = useState(false);

  useEffect(() => {
  if (selectedCaseId) {
    const caseToOpen = archivedCases.find(c => c.id === selectedCaseId);
    if (caseToOpen) {
      handleOpenDocument(caseToOpen);
      if (onCaseViewed) onCaseViewed();
    }
  }
}, [selectedCaseId]);

  
  // Del opp i vanlige arkiverte og reklamasjoner
  const normalArchivedCases = archivedCases.filter(case_ => !case_.reklamasjon);
  const reklamasjonArchivedCases = archivedCases.filter(case_ => case_.reklamasjon);

  // Vis riktig liste basert på aktiv mappe
 const displayedCases = activeFolder === "reklamasjon" 
  ? reklamasjonArchivedCases 
  : activeFolder === "alle"
  ? archivedCases
  : normalArchivedCases;

  const handleOpenDocument = (case_: Case) => {
    setSelectedCase(case_);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setSelectedCase(null);
    setShowDetails(false);
  };

  const handleCreateCopy = async (caseId: number) => {
    const caseToCopy = cases.find(c => c.id === caseId);
    if (!caseToCopy) return;

    const confirm = window.confirm(
      `📋 OPPRETT KOPI\n\n` +
      `Dette vil opprette en ny ordre basert på:\n` +
      `Kunde: ${caseToCopy.kunde}\n` +
      `Maskin: ${caseToCopy.maskinMerke}\n\n` +
      `Den nye ordren vil starte i "Ny registrering".\n` +
      `Er du sikker?`
    );

    if (!confirm) return;

    // Opprett kopi med ny ID og dato
    const newCase: Case = {
      ...caseToCopy,
      id: Date.now(),
      dato: new Date().toISOString().split("T")[0],
      kategori: "ny-registrering",
      opprettet: new Date().toISOString(),
      // Nullstill status-felter
      delebestillingDato: "",
      utfortDato: "",
      serviceRepUtført: "",
      utlevertAv: "",
      betalt: false,
      faktura: false,
      betalingsmetode: null,
      reklamasjon: false, // Nullstill reklamasjon
      // Legg til referanse til original
      kommentar: `Kopi av ordre #${caseId} - ${caseToCopy.kommentar || ""}`.trim()
    };

    // Lagre ny ordre
    setCases(prev => [...prev, newCase]);
    await supabaseService.saveCase(newCase);
    
    // Åpne for redigering
    openCaseForEditing(newCase);
    
    // Lukk arkiv-visning hvis åpen
    if (showDetails) {
      handleCloseDetails();
    }
    
    alert(`✅ Kopi opprettet! Du kan nå redigere den nye ordren.`);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="text-gray-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">Arkiv</h2>
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
              {archivedCases.length} saker totalt
            </span>
          </div>
        </div>
      </div>

      {/* Mappevelger */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveFolder("alle")}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeFolder === "alle"
                ? "bg-white shadow-sm border border-gray-300 text-gray-900"
                : "text-gray-600 hover:bg-white hover:shadow-sm"
            }`}
          >
            <FolderOpen size={18} />
            <span>Alle arkiverte</span>
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
              {archivedCases.length}
            </span>
          </button>

          <button
            onClick={() => setActiveFolder("reklamasjon")}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeFolder === "reklamasjon"
                ? "bg-white shadow-sm border border-red-300 text-red-900"
                : "text-gray-600 hover:bg-white hover:shadow-sm"
            }`}
          >
            <AlertCircle size={18} className="text-red-600" />
            <span>Reklamasjoner</span>
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
              {reklamasjonArchivedCases.length}
            </span>
          </button>
        </div>
      </div>

      {moveError && (
        <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="text-red-600" size={16} />
          <span className="text-red-700 text-sm">{moveError}</span>
        </div>
      )}

      <div className="p-4">
        {displayedCases.length === 0 ? (
          <div className="text-center py-12">
            {activeFolder === "reklamasjon" ? (
              <>
                <AlertCircle className="mx-auto text-gray-400 mb-3" size={48} />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Ingen arkiverte reklamasjoner
                </h3>
                <p className="text-gray-600">
                  Reklamasjonssaker vil vises her når de arkiveres.
                </p>
              </>
            ) : (
              <>
                <FileText className="mx-auto text-gray-400 mb-3" size={48} />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Ingen arkiverte saker
                </h3>
                <p className="text-gray-600">
                  Fullførte saker vil vises her når de arkiveres.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Maskin
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Beskrivelse
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opprettet
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utført av
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Betalingsstatus
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedCases.map((case_) => (
                  <tr key={case_.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="text-gray-400 mr-2" size={16} />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {case_.kunde}
                          </div>
                          <div className="text-sm text-gray-500">
                            {case_.tlf}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="text-gray-400 mr-2" size={16} />
                        <div>
                          <div className="text-sm text-gray-900">
                            {case_.maskinMerke || "Ikke spesifisert"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {case_.serienr || "-"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">
                        {case_.beskrivelse.length > 50
                          ? case_.beskrivelse.substring(0, 50) + "..."
                          : case_.beskrivelse}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {case_.reklamasjon && (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                          ⚠️ Reklamasjon
                        </span>
                      )}
                      {!case_.reklamasjon && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          ✅ Fullført
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="text-gray-400 mr-2" size={16} />
                        <span className="text-sm text-gray-900">
                          {new Date(case_.opprettet).toLocaleDateString("nb-NO")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {case_.serviceRepUtført || case_.utlevertAv || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          case_.betalingsmetode === 'kasse'
                            ? "bg-green-100 text-green-800"
                            : case_.betalingsmetode === 'faktura'
                            ? "bg-blue-100 text-blue-800"
                            : case_.betalt
                            ? "bg-green-100 text-green-800"
                            : case_.faktura
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {case_.betalingsmetode === 'kasse' 
                          ? "💵 Kontant" 
                          : case_.betalingsmetode === 'faktura'
                          ? "📄 Faktura"
                          : case_.betalt
                          ? "💵 Betalt"
                          : case_.faktura
                          ? "📄 Fakturert"
                          : "❓ Ikke spesifisert"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleOpenDocument(case_)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleCreateCopy(case_.id)}
                        className="text-green-600 hover:text-green-900"
                        title="Opprett kopi"
                      >
                        📋
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">
                  Arkivert ordre
                </h3>
                <button
                  onClick={handleCloseDetails}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">Kundeinformasjon</h4>
                  <div className="space-y-2">
                    <p><span className="text-gray-600">Navn:</span> <span className="font-medium">{selectedCase.kunde}</span></p>
                    <p><span className="text-gray-600">Telefon:</span> {selectedCase.tlf || "-"}</p>
                    <p><span className="text-gray-600">E-post:</span> {selectedCase.mail || "-"}</p>
                    <p><span className="text-gray-600">Org.nr:</span> {selectedCase.orgnr || "-"}</p>
                    <p><span className="text-gray-600">Adresse:</span> {selectedCase.adresse || "-"}</p>
                    <p><span className="text-gray-600">Post:</span> {selectedCase.postnrSted || "-"}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700 mb-3">Maskindetaljer</h4>
                  <div className="space-y-2">
                    <p><span className="text-gray-600">Merke:</span> <span className="font-medium">{selectedCase.maskinMerke || "-"}</span></p>
                    <p><span className="text-gray-600">Serienr:</span> {selectedCase.serienr || "-"}</p>
                    <p><span className="text-gray-600">Årsmodell:</span> {selectedCase.arsmodel || "-"}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
  <h4 className="font-semibold text-gray-700 mb-3">Beskrivelse</h4>
  <p className="text-gray-600 bg-gray-50 p-3 rounded">{selectedCase.beskrivelse}</p>
</div>

{selectedCase.kommentar && (
  <div className="mt-6">
    <h4 className="font-semibold text-gray-700 mb-3">Kommentar</h4>
    <p className="text-gray-600 bg-gray-50 p-3 rounded">{selectedCase.kommentar}</p>
  </div>
)}

{/* LEGG TIL VARELINJER HER */}
{selectedCase.varelinjer &&
  selectedCase.varelinjer.some(
    (v) => v.varebeskrivelse || v.varenr
  ) && (
    <div className="mt-6">
      <h4 className="font-semibold text-gray-700 mb-3">Varelinjer/Kostnader</h4>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Dato
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Antall
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Varenr
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Beskrivelse
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Eks. MVA
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Inkl. MVA
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Sum
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {selectedCase.varelinjer
              .filter((v) => v.varebeskrivelse || v.varenr)
              .map((linje, index) => (
                <tr key={index}>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {linje.dato}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {linje.antall}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {linje.varenr}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {linje.varebeskrivelse}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {linje.eksklMva}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900">
                    {linje.inklMva}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900 font-medium">
                    {linje.sum}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )}

<div className="mt-6">
  <h4 className="font-semibold text-gray-700 mb-3">Status</h4>
  <div className="flex items-center space-x-3">
    <span
      className={`px-3 py-1 rounded text-sm font-medium ${
        selectedCase.betalingsmetode === 'kasse'
          ? "bg-green-100 text-green-800"
          : selectedCase.betalingsmetode === 'faktura'
          ? "bg-blue-100 text-blue-800"
          : selectedCase.betalt
                        ? "bg-green-100 text-green-800"
                        : selectedCase.faktura
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {selectedCase.betalingsmetode === 'kasse' 
                      ? "💵 Betalt kontant i kassen" 
                      : selectedCase.betalingsmetode === 'faktura'
                      ? "📄 Sendt faktura til kunde"
                      : selectedCase.betalt
                      ? "💵 Betalt"
                      : selectedCase.faktura
                      ? "📄 Fakturert"
                      : "❓ Betalingsstatus ikke spesifisert"}
                  </span>
                  {selectedCase.reklamasjon && (
                    <span className="ml-3 px-3 py-1 bg-red-100 text-red-800 rounded text-sm font-medium">
                      ⚠️ Reklamasjon
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-between">
                <div className="space-x-3">
                  <button
                    onClick={() => handleCreateCopy(selectedCase.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    📋 Opprett kopi av ordre
                  </button>
                </div>
                <div className="space-x-3">
                  <button
                    onClick={() => {
                      alert(
                        `🔒 ARKIVERT ORDRE!\n\n` +
                        `Arkiverte ordre kan ikke redigeres.\n\n` +
                        `Tips: Du kan opprette en kopi hvis du trenger å lage en lignende ordre.`
                      );
                    }}
                    className="px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed"
                  >
                    Låst - Kan ikke redigeres
                  </button>
                  <button
                    onClick={handleCloseDetails}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Lukk
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Arkiv;